import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { NFConferenciaTable } from './NFConferenciaTable';
import { NFForm } from './NFForm';
import { PdfViewer, type PdfEntry } from './PdfViewer';
import { fmtDate, fmtMoney2 } from '../lib/fmt';
import { nfSeverity } from '../lib/nfStatus';
import type { AggregateConferencia, NFOut, ParcelaOut, PedidoOut } from '../types/api';

interface PedidoConferenciaModalProps {
  pedido: PedidoOut;
  nfs: NFOut[];
  onClose: () => void;
  /** Abre já na aba de formulário: NFOut edita, 'new' anexa, null fica no Resumo. */
  initialNF?: NFOut | 'new' | null;
}

type TabKey = 'resumo' | 'nfs' | 'form' | 'conferencia' | 'pdfs';

const SEV_BADGE: Record<string, string> = {
  conferido: 'bg-emerald-500/25 text-emerald-300',
  divergente: 'bg-rose-500/25 text-rose-300',
  pendente: 'bg-mbc-mute/25 text-mbc-soft',
  bonificacao: 'bg-emerald-500/15 text-emerald-300',
};

const SEV_LABEL: Record<string, string> = {
  conferido: '✓ conferido',
  divergente: '⚠ divergente',
  pendente: '⏳ pendente',
  bonificacao: '🎁 bonificação',
};

export function PedidoConferenciaModal({ pedido, nfs, onClose, initialNF = null }: PedidoConferenciaModalProps) {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>(initialNF ? 'form' : 'resumo');
  // NFOut = editando existente · 'new' = anexar nova · null = nada selecionado ainda
  const [editingNF, setEditingNF] = useState<NFOut | 'new' | null>(initialNF);

  const comuns = useMemo(() => nfs.filter(nf => nf.tipo_nf !== 'bonificacao'), [nfs]);
  const isMulti = comuns.length > 1;

  const { data: parcelas = [] } = useQuery<ParcelaOut[]>({
    queryKey: ['pedido-parcelas-nf', pedido.id],
    queryFn: () => api.get<ParcelaOut[]>(`/pedidos/${pedido.id}/parcelas`).then(r => r.data),
  });

  // Conferência consolidada calculada no backend (só faz sentido no multi-NF).
  const { data: agg = null } = useQuery<AggregateConferencia>({
    queryKey: ['pedido-conferencia', pedido.id, nfs.length],
    enabled: isMulti,
    queryFn: () => api.get<AggregateConferencia>(`/pedidos/${pedido.id}/conferencia`).then(r => r.data),
  });

  const valorNfs = comuns.reduce((sum, nf) => sum + Number(nf.valor_total), 0);
  const valorPedido = Number(pedido.valor_total);
  const pctFaturado = valorPedido > 0 ? (valorNfs / valorPedido) * 100 : 0;

  const totalPedidoParcelas = parcelas.reduce((sum, item) => sum + Number(item.valor), 0);
  const totalFaturas = agg?.totals.faturas ?? 0;
  const totalBoletoReal = agg?.totals.boletoReal ?? 0;

  const removeMut = useMutation({
    mutationFn: (nfId: number) => api.delete(`/nfs/${nfId}/pdf`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nfs'] });
    },
  });

  const pdfEntries = useMemo<PdfEntry[]>(() => {
    const entries: PdfEntry[] = [];
    if (pedido.pdf_meta?.has_pdf) {
      entries.push({ key: 'pedido', label: `Pedido ${pedido.numero}`, path: `/pedidos/${pedido.id}/pdf`, fileName: `pedido_${pedido.numero}.pdf` });
    }
    nfs.forEach(nf => {
      if (nf.pdf_meta?.has_pdf) {
        entries.push({ key: `nf-${nf.id}`, label: `NF ${nf.numero}`, path: `/nfs/${nf.id}/pdf`, fileName: `nf_${nf.numero}.pdf`, nfId: nf.id });
      }
    });
    return entries;
  }, [nfs, pedido]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  function openForm(nf: NFOut | 'new') {
    setEditingNF(nf);
    setActiveTab('form');
  }

  function backToList() {
    setEditingNF(null);
    setActiveTab('nfs');
  }

  const valorBadge = !agg
    ? 'bg-mbc-mute/25 text-mbc-soft'
    : agg.valorStatus === 'conferido'
      ? 'bg-emerald-500/25 text-emerald-300'
      : agg.valorStatus === 'excedido'
        ? 'bg-rose-500/25 text-rose-300'
        : 'bg-amber-500/20 text-amber-300';
  const valorLabel = !agg ? '—' : agg.valorStatus === 'conferido' ? '✓ valor conferido' : agg.valorStatus === 'excedido' ? '⚠ soma maior que o pedido' : '⏳ falta faturar';

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'resumo', label: 'Resumo' },
    { key: 'nfs', label: `NFs (${nfs.length})` },
    ...(editingNF !== null ? [{ key: 'form' as TabKey, label: editingNF !== 'new' ? 'Editar NF' : 'Importar NF' }] : []),
    ...(isMulti ? [{ key: 'conferencia' as TabKey, label: 'Conferência' }] : []),
    { key: 'pdfs', label: `PDFs (${pdfEntries.length})` },
  ];

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-end md:place-items-center p-2 md:p-4 pointer-events-none">
        <div className="card glow pointer-events-auto w-full max-w-5xl overflow-y-auto rounded-2xl p-4 md:p-6" style={{ maxHeight: '96vh' }}>
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-extrabold text-white">📋 Conferência do pedido {pedido.numero}</h2>
              <p className="mt-1 text-xs text-mbc-mute">
                {pedido.fornecedor} · {comuns.length} NF(s) de produto{isMulti ? ' · conferência consolidada' : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-3xl leading-none text-mbc-mute hover:text-white">×</button>
          </div>

          {/* Barra de abas */}
          <div className="mb-5 flex flex-wrap gap-1 border-b border-mbc-line">
            {tabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`-mb-px rounded-t-lg border-b-2 px-4 py-2 text-sm font-semibold transition ${
                  activeTab === tab.key
                    ? 'border-mbc-orange text-white'
                    : 'border-transparent text-mbc-mute hover:text-mbc-soft'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Aba: Resumo */}
          {activeTab === 'resumo' && (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="card p-3">
                  <div className="text-[10px] uppercase tracking-wider text-mbc-mute">Valor do pedido</div>
                  <div className="mt-1 text-lg font-bold text-white">{fmtMoney2(valorPedido)}</div>
                </div>
                <div className="card p-3">
                  <div className="text-[10px] uppercase tracking-wider text-mbc-mute">Total NFs</div>
                  <div className="mt-1 text-lg font-bold text-white">{fmtMoney2(valorNfs)}</div>
                  <div className={`text-xs ${pctFaturado >= 99 && pctFaturado <= 101 ? 'text-emerald-300' : pctFaturado >= 50 ? 'text-mbc-bluel' : 'text-amber-300'}`}>{pctFaturado.toFixed(1)}% faturado</div>
                </div>
                <div className="card p-3 flex flex-col justify-center">
                  <div className="text-[10px] uppercase tracking-wider text-mbc-mute">Conferência de valor</div>
                  {isMulti ? (
                    <span className={`badge mt-1 w-fit ${valorBadge}`}>{valorLabel}</span>
                  ) : (
                    <span className="mt-1 text-xs text-mbc-soft">NF única — conferida individualmente.</span>
                  )}
                </div>
              </div>

              {isMulti && agg && agg.divergencias.length > 0 && (
                <div className="card p-4" style={{ background: 'rgba(244,63,94,.05)', borderColor: 'rgba(244,63,94,.3)' }}>
                  <div className="mb-2 text-sm font-bold text-rose-300">⚠ {agg.divergencias.length} divergência(s) no consolidado</div>
                  <div className="space-y-2">
                    {agg.divergencias.map((div, idx) => (
                      <div key={`${div.titulo}-${idx}`} className="rounded-lg border border-white/5 bg-black/10 p-2">
                        <div className="text-sm font-semibold text-white">{div.titulo}</div>
                        <div className="mt-0.5 text-xs text-mbc-soft">{div.detalhe}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Aba: NFs */}
          {activeTab === 'nfs' && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider text-mbc-orangel">📑 Notas fiscais</div>
                <button type="button" onClick={() => openForm('new')} className="rounded bg-mbc-orange/20 px-3 py-1 text-xs text-mbc-orangel hover:bg-mbc-orange/40">＋ Anexar NF</button>
              </div>
              {nfs.length === 0 ? (
                <p className="py-3 text-center text-xs text-mbc-mute">Nenhuma NF anexada a este pedido.</p>
              ) : (
                <table className="dt" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>NF</th>
                      <th>Emitente</th>
                      <th>Emissão</th>
                      <th>Competência</th>
                      <th className="num">Valor</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfs.map(nf => {
                      const sev = nfSeverity(nf);
                      return (
                        <tr key={nf.id}>
                          <td className="font-mono text-xs">{nf.numero}</td>
                          <td><span className="truncate inline-block max-w-[180px]" title={nf.emitente}>{nf.emitente}</span></td>
                          <td>{fmtDate(nf.data_emissao)}</td>
                          <td className={nf.data_competencia ? 'text-mbc-bluel' : 'text-mbc-mute'}>
                            {fmtDate(nf.data_competencia ?? nf.data_emissao)}
                          </td>
                          <td className="num">{fmtMoney2(Number(nf.valor_total))}</td>
                          <td className="text-center"><span className={`badge ${SEV_BADGE[sev]}`}>{SEV_LABEL[sev]}</span></td>
                          <td className="text-center">
                            <button type="button" onClick={() => openForm(nf)} className="rounded bg-mbc-bluel/15 px-2.5 py-1 text-xs text-mbc-bluel hover:bg-mbc-bluel/30">Editar</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Aba: Importar/Editar (formulário) */}
          {activeTab === 'form' && (
            <NFForm
              key={editingNF && editingNF !== 'new' ? `nf-${editingNF.id}` : 'new'}
              lockPedido
              pedidos={[pedido]}
              existingNF={editingNF && editingNF !== 'new' ? editingNF : null}
              suggestedPedidoId={pedido.id}
              onSaved={backToList}
              onDeleted={backToList}
              onCancel={backToList}
            />
          )}

          {/* Aba: Conferência consolidada (multi-NF) */}
          {activeTab === 'conferencia' && isMulti && agg && (
            <div>
              <NFConferenciaTable
                pedido={pedido}
                parcelasPedidoCount={parcelas.length}
                faturasCount={agg.faturasCount}
                rows={agg.rows}
                statusConferencia={agg.datasStatus}
                totalPedidoParcelas={totalPedidoParcelas}
                totalFaturas={totalFaturas}
                totalBoletoReal={totalBoletoReal}
                onBoletoRealChange={() => { /* consolidado é somente leitura — edite o boleto real na aba "Editar NF" da nota correspondente */ }}
                showNfColumn
                competenciaAjustes={agg.competenciaAjustes}
              />
              <p className="mt-2 text-[10px] text-mbc-mute">
                💡 Visão consolidada das {comuns.length} NFs. Para ajustar a <b>data/valor do boleto real</b>, abra a NF correspondente na aba <b>NFs → Editar</b>.
              </p>
            </div>
          )}

          {/* Aba: PDFs */}
          {activeTab === 'pdfs' && (
            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-mbc-mute">PDFs importados</div>
              <PdfViewer
                entries={pdfEntries}
                onRemove={entry => entry.nfId != null && removeMut.mutate(entry.nfId)}
                removing={removeMut.isPending}
                emptyLabel="Nenhum PDF anexado a este pedido ou às suas NFs."
              />
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-mbc-line bg-mbc-panel2 px-5 py-2.5 text-mbc-soft hover:text-white">Fechar</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
